import { useEffect, useMemo, useState } from "react";
import { APIResponse } from "@/lib/apiFetch";
import { RecurringPlansResponse, routes } from "@/services/recurringPlans";
import { fetcher } from "@/lib/apiFetch";
import useSWR, { preload } from "swr";
import { fetcherV2 } from "@/services/api";

interface Props {
  initialPage: number;
  limit: number;
  sortBy: string;
  sortDirection: "ASC" | "DESC";
  select?: string[];
  campaignId?: number;
  status?: {
    in?: string[];
    notIn?: string[];
  };
}

export function useRecurringPlans({
  initialPage,
  limit,
  sortBy,
  sortDirection,
  campaignId,
  status,
}: Props) {
  const [page, setPage] = useState<number>(initialPage);
  const [pages, setPages] = useState<number>(0);
  const { data, error, mutate, isLoading } = useSWR(
    () => [
      routes.recurringPlansList,
      { page, limit, sortBy, sortDirection, campaignId, status },
    ],
    fetcherV2<RecurringPlansResponse>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
    }
  );

  useEffect(() => {
    if (data && !isLoading) {
      setPages(data.pages);
    }
  }, [data, isLoading]);

  const preloadData = (increment: -1 | 1) => {
    preload(
      [
        routes.recurringPlansList,
        {
          page: page + increment,
          limit,
          sortBy,
          sortDirection,
          campaignId,
        },
      ],
      fetcherV2
    );
  };

  return {
    plans: data?.result ?? [],
    total: data?.total ?? 0,
    page: page + 1,
    pages: pages,
    next: () => setPage((p) => p + 1),
    previous: () => setPage((p) => p - 1),
    canPrevious: page > 0,
    canNext: page + 1 < pages,
    isLoading: !error && !data,
    isError: error,
    pageSize: limit,
    refresh: mutate,
    preloadData,
  };
}
